import React from 'react';
import { FormattedMessage } from 'react-intl';
import objectHash from 'object-hash';
import PropTypes from 'prop-types';

import { legShape, seatsShape, passengerShape } from 'constants/propTypes';
import { types } from 'types/passengers';
import PassengerType from 'components/ui/PassengerType';
import GetMealsQuery from 'components/queries/GetMealsQuery';
import { mealTypesToDisplay, customMealCode } from 'types/meals';
import { removeLeadingZeros } from 'utils/functions';
import findCollectionByPassengerId from 'utils/findCollectionByPassengerId';
import JourneySegment from 'components/shared/JourneySegment';

import { CenterAlign, UnselectedPassengerButton, PassengerButton, StyledActionButton } from './SegmentContainer.styles';
import MealsSelect from '../MealsSelect';
import { isNoMealRoute } from 'utils/meals';

const columns = (selectMeal, onSelectNewPassenger, onRemovePassengerSelection, nonCancelledBookingItems) => [
  { key: 'firstName' },
  { key: 'lastName' },
  {
    key: 'passengerType',
    format: (type, { disabilityType }) => <PassengerType type={type} disabilityType={disabilityType} />
  },
  {
    key: 'coachSeat',
    format: (passengerSeat, { id, selected, isSeatEditable }) => {
      const { seatCoach, type } = passengerSeat;

      return type !== types.INFANT && seatCoach ? (
        <CenterAlign>
          {selected ? (
            <PassengerButton
              data-cy="passenger-button"
              onClick={(e) => {
                e.preventDefault();
                onRemovePassengerSelection(id);
              }}
            >
              {seatCoach}
            </PassengerButton>
          ) : (
            <UnselectedPassengerButton
              data-cy="passenger-button"
              disabled={!isSeatEditable}
              onClick={(e) => {
                e.preventDefault();
                onSelectNewPassenger(id);
              }}
            >
              {seatCoach}
            </UnselectedPassengerButton>
          )}
        </CenterAlign>
      ) : (
        ' -- -- '
      );
    }
  },
  { key: 'classOfService' },
  {
    key: 'meal',
    format: (passengerMeal, { isMealEditable }) => {
      const { mealCode, meals, type, ...mealProps } = passengerMeal;
      const defaultMeal = mealCode !== customMealCode.REGULAR_MEAL ? mealCode : customMealCode.REGULAR_MEAL;
      const isMealsAvailable =
        passengerMeal.mealCode !== customMealCode.NO_MEAL &&
        type !== types.INFANT &&
        !isNoMealRoute(nonCancelledBookingItems?.[0]?.origin?.uic, nonCancelledBookingItems?.[0]?.destination?.uic);

      return isMealsAvailable ? (
        <CenterAlign>
          <MealsSelect defaultValue={defaultMeal} disabled={!isMealEditable} {...{ meals, selectMeal, ...mealProps }} />
        </CenterAlign>
      ) : (
        <StyledActionButton data-cy="no-meal-btn" disabled>
          <FormattedMessage id={`mealTypes.${mealTypesToDisplay.NO_MEAL}`} />
        </StyledActionButton>
      );
    }
  }
];
const findPaxSeat = (passengerId, seats) => seats.find((item) => item.passenger.id === passengerId);

const mapToPassengersData =
  (seats, meals, selectedPassenger, selectedPassengers, bookingItems) =>
  ({ passenger, passenger: { id, firstName, lastName, type, disabilityType }, mealCode, inventoryClass }, _, { classOfService }) => {
    const seat = findPaxSeat(id, seats);
    const booking = findCollectionByPassengerId(id, bookingItems);
    const seatCoach = seat && `${seat.coach}  /  ${removeLeadingZeros(seat.number)}`;

    return {
      key: objectHash(passenger),
      id,
      firstName,
      lastName,
      passengerType: type,
      disabilityType,
      coachSeat: { seatCoach, type },
      classOfService: type !== types.INFANT ? classOfService?.name ?? ' -- ' : ' -- ',
      meal: { mealCode, meals, type, lastName, firstName, id },
      isSeatEditable: seat && seat.editable,
      isMealEditable: booking && booking.isMealEditable,
      selected:
        (selectedPassenger && passenger.id === selectedPassenger.id) ||
        (selectedPassengers?.length && selectedPassengers.some(({ id: selectedId }) => passenger.id === selectedId))
    };
  };

const SegmentContainer = ({
  segmentId,
  bookingReference,
  currency,
  journey,
  seats,
  selectMeal,
  selectedPassenger,
  selectedPassengers,
  onSelectNewPassenger,
  onRemovePassengerSelection
}) => {
  const nonCancelledBookingItems = journey.bookingItems.filter((item) => !item.cancelled);

  return (
    <GetMealsQuery reference={bookingReference} segmentId={segmentId}>
      {(meals) => (
        <JourneySegment
          bookingReference={bookingReference}
          numberOfPassengers={nonCancelledBookingItems.length}
          journey={journey}
          currency={currency}
          columns={columns(selectMeal, onSelectNewPassenger, onRemovePassengerSelection, nonCancelledBookingItems)}
          buildRow={mapToPassengersData(seats, meals, selectedPassenger, selectedPassengers, nonCancelledBookingItems)}
          bookingItems={nonCancelledBookingItems}
        />
      )}
    </GetMealsQuery>
  );
};

SegmentContainer.propTypes = {
  currency: PropTypes.string.isRequired,
  bookingReference: PropTypes.string.isRequired,
  journey: legShape,
  seats: seatsShape,
  segmentId: PropTypes.string.isRequired,
  selectedPassenger: passengerShape,
  selectedPassengers: PropTypes.arrayOf(passengerShape),
  selectMeal: PropTypes.func,
  onSelectNewPassenger: PropTypes.func,
  onRemovePassengerSelection: PropTypes.func
};

export default SegmentContainer;
